import type { InsightsInsightsPageQuery } from 'generated/resources/graphql'
import capitalize from 'lodash/capitalize'

type ContractsPromotion =
  InsightsInsightsPageQuery['contracts']['promotionsConnection']['nodes'][number]
type ContractsPromotionStat =
  InsightsInsightsPageQuery['contracts']['promotionStatsConnection']['nodes'][number]

import i18n from 'modules/core/lib/i18n'

import { Export, ExportRow, GenerateExport } from './export'

const calculatePromotionsByTeam = (
  promotionsByTeam: ContractsPromotion[],
  prevMonthTotals?: ContractsPromotionStat
) => {
  let promotionsByStatus = promotionsByTeam.map((entry) => ({
    label: entry.team,
    values: [
      { label: i18n.t('insights.widgets.approved_promotions.approved'), value: entry.approved },
      { label: i18n.t('insights.widgets.approved_promotions.pending'), value: entry.pending },
      { label: i18n.t('insights.widgets.approved_promotions.rejected'), value: entry.rejected },
    ],
    total: entry.approved + entry.rejected + entry.pending,
  }))
  promotionsByStatus = [...promotionsByStatus].sort((a, b) => b.total - a.total)

  const approved = promotionsByTeam.reduce((acc, entry) => acc + entry.approved, 0)
  const totalRequestedThisMonth = promotionsByStatus.reduce((acc, entry) => acc + entry.total, 0)

  const approvedLastMonth = prevMonthTotals?.approvedPrevMonth ?? 0
  const totalRequestedLastMonth = prevMonthTotals?.requestedPrevMonth ?? 0

  return {
    promotionsByStatus,
    approved,
    totalRequestedThisMonth,
    approvedLastMonth,
    totalRequestedLastMonth,
  }
}

export default class PromotionsByTeam implements GenerateExport {
  private promotionsByTeam: ContractsPromotion[]
  private prevMonthTotals?: ContractsPromotionStat

  constructor(promotionsByTeam: ContractsPromotion[], prevMonthTotals?: ContractsPromotionStat) {
    this.promotionsByTeam = promotionsByTeam
    this.prevMonthTotals = prevMonthTotals
  }

  isEmpty() {
    return !this.promotionsByTeam.length
  }

  export(): Export {
    const { promotionsByStatus } = this.generate()

    const data: ExportRow[][] = [
      [
        capitalize(i18n.t('common.team')),
        i18n.t('common.status'),
        i18n.t('insights.widgets.shared.headcount'),
      ],
    ]

    promotionsByStatus.forEach(({ label: team, values }) => {
      values.forEach(({ label: status, value: headcount }) => {
        data.push([team, status, headcount])
      })
    })

    return {
      title: i18n.t('insights.widgets.approved_promotions.title'),
      data,
    }
  }

  generate() {
    return calculatePromotionsByTeam(this.promotionsByTeam, this.prevMonthTotals)
  }
}
